import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeftIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PushPanelShell from './PushPanelShell';
import PushNotificationDevicePreview from './PushNotificationDevicePreview';
import StatusBadge from './StatusBadge';
import { PUSH_HUB_BASE_PATH } from '../../../models/MasterAdmin/pushNotificationHub';
import { formatTargetPlatformLabel } from '../../../models/MasterAdmin/pushNotificationCampaign';
import { getNotificationScreenLabel } from '../../../models/MasterAdmin/notificationScreenTypes';
import {
  fetchCampaignAnalysis,
  fetchCampaignExecutions,
  getCampaign,
} from '../../../services/MasterAdmin/pushNotificationCampaignService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import type { PushNotificationCampaign, PushCampaignExecution } from '../../../models/MasterAdmin/pushNotificationCampaign';
import type { PushDeliveryLog } from '../../../models/MasterAdmin/pushNotificationHub';

type TabId = 'overview' | 'participants' | 'executions';

const STATUS_COLORS: Record<string, string> = {
  queued: 'bg-gray-100 text-gray-700',
  sent: 'bg-sky-100 text-sky-800',
  delivered: 'bg-emerald-100 text-emerald-800',
  read: 'bg-indigo-100 text-indigo-800',
  failed: 'bg-red-100 text-red-800',
  skipped: 'bg-amber-100 text-amber-800',
};

function StatCard({
  label,
  value,
  active,
  onClick,
}: {
  label: string;
  value: number;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border bg-white p-3 text-left dark:bg-gray-900 ${
        active ? 'border-blue-600 ring-1 ring-blue-600' : 'border-gray-200 dark:border-gray-700'
      }`}
    >
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">{value.toLocaleString()}</p>
    </button>
  );
}

const PushCampaignDetailPage: React.FC = () => {
  const { campaignId = '' } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>('overview');
  const [campaign, setCampaign] = useState<PushNotificationCampaign | null>(null);
  const [analysis, setAnalysis] = useState<{
    analysis: {
      attempted: number;
      sent: number;
      delivered: number;
      read: number;
      failed: number;
      queued: number;
      skipped: number;
      deliveryRatePct: number;
      readRatePct: number;
      failRatePct: number;
    };
    participants: PushDeliveryLog[];
    participantTotal: number;
    campaign?: { templateName?: string | null };
  } | null>(null);
  const [executions, setExecutions] = useState<PushCampaignExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [participantStatus, setParticipantStatus] = useState('');

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const [campRes, analysisRes, execRes] = await Promise.all([
        getCampaign(campaignId),
        fetchCampaignAnalysis(campaignId, { limit: 50, status: participantStatus || undefined }),
        fetchCampaignExecutions(campaignId, { page: 1, limit: 20 }),
      ]);
      setCampaign(campRes.data);
      setAnalysis(analysisRes.data);
      setExecutions(execRes.data?.items || []);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load campaign'));
    } finally {
      setLoading(false);
    }
  }, [campaignId, participantStatus]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !campaign) {
    return (
      <PushPanelShell title="Campaign">
        <p className="text-sm text-gray-500">Loading…</p>
      </PushPanelShell>
    );
  }

  if (!campaign) {
    return (
      <PushPanelShell title="Campaign">
        <p className="text-sm text-red-600">Campaign not found.</p>
        <Link to={PUSH_HUB_BASE_PATH} className="mt-2 inline-block text-sm text-blue-600">
          Back to campaigns
        </Link>
      </PushPanelShell>
    );
  }

  const a = analysis?.analysis;
  const templateName = analysis?.campaign?.templateName;

  return (
    <PushPanelShell
      title={campaign.title}
      description={templateName ? `Template: ${templateName}` : 'Campaign overview, delivery analytics, and participant log'}
      actions={
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate(PUSH_HUB_BASE_PATH)}
            className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm dark:border-gray-600"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>
          <button
            type="button"
            onClick={() => navigate(`${PUSH_HUB_BASE_PATH}/campaigns/${campaign.id}/edit`)}
            className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white"
          >
            <PencilSquareIcon className="h-4 w-4" />
            Edit
          </button>
        </div>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <StatusBadge status={campaign.status} />
        <span className="text-xs text-gray-500">{formatTargetPlatformLabel(campaign.targetPlatform)}</span>
        <span className="font-mono text-xs text-gray-400">{campaign.id}</span>
      </div>

      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-700">
        {(
          [
            ['overview', 'Overview'],
            ['participants', 'Participants'],
            ['executions', 'Executions'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`shrink-0 px-3 py-2 text-sm font-medium ${
              tab === id
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_300px]">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              <StatCard label="Attempted" value={a?.attempted || 0} onClick={() => { setParticipantStatus(''); setTab('participants'); }} />
              <StatCard label="Delivered" value={a?.delivered || 0} onClick={() => { setParticipantStatus('DELIVERED'); setTab('participants'); }} />
              <StatCard label="Read" value={a?.read || 0} onClick={() => { setParticipantStatus('READ'); setTab('participants'); }} />
              <StatCard label="Failed" value={a?.failed || 0} onClick={() => { setParticipantStatus('FAILED'); setTab('participants'); }} />
              <StatCard label="Queued" value={a?.queued || 0} onClick={() => { setParticipantStatus('QUEUED'); setTab('participants'); }} />
              <StatCard label="Skipped" value={a?.skipped || 0} onClick={() => { setParticipantStatus('SKIPPED'); setTab('participants'); }} />
              <StatCard label="Delivery %" value={a?.deliveryRatePct || 0} />
              <StatCard label="Read %" value={a?.readRatePct || 0} />
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Message</p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{campaign.message}</p>
              <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-gray-500">Audience</dt>
                  <dd>{campaign.targetAudience}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Opens</dt>
                  <dd>{getNotificationScreenLabel(campaign.intentType)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Template</dt>
                  <dd>{templateName || '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Type</dt>
                  <dd>{campaign.notificationType}</dd>
                </div>
              </dl>
            </div>
          </div>
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
            <PushNotificationDevicePreview
              title={campaign.title}
              message={campaign.message}
              imageUrl={campaign.imageUrl}
              bannerImageUrl={campaign.bannerImageUrl}
            />
          </div>
        </div>
      ) : null}

      {tab === 'participants' ? (
        <div>
          <div className="mb-3 flex flex-wrap gap-2">
            {['', 'DELIVERED', 'READ', 'FAILED', 'QUEUED', 'SKIPPED'].map((s) => (
              <button
                key={s || 'all'}
                type="button"
                onClick={() => setParticipantStatus(s)}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  participantStatus === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 dark:bg-gray-800'
                }`}
              >
                {s || 'All'}
              </button>
            ))}
          </div>
          <p className="mb-2 text-xs text-gray-500">{analysis?.participantTotal?.toLocaleString() || 0} deliveries</p>
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Recipient</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Platform</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Error</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {(analysis?.participants || []).length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                      No participant deliveries yet.
                    </td>
                  </tr>
                ) : (
                  (analysis?.participants || []).map((row) => (
                    <tr key={row.id}>
                      <td className="px-4 py-3">
                        <p className="font-medium">{row.recipientName}</p>
                        <p className="text-xs text-gray-500">
                          {row.recipientType}
                          {row.recipientPhone ? ` · ${row.recipientPhone}` : ''}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                            STATUS_COLORS[(row.displayStatus || row.status || '').toLowerCase()] || STATUS_COLORS.queued
                          }`}
                        >
                          {row.displayStatus || row.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">{row.platform || '—'}</td>
                      <td className="px-4 py-3 text-xs text-red-600">{row.errorMessage || row.fcmErrorCode || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {row.sentAt || row.createdAt
                          ? new Date(row.sentAt || row.createdAt).toLocaleString('en-IN')
                          : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === 'executions' ? (
        <div className="space-y-2">
          {executions.length === 0 ? (
            <p className="text-sm text-gray-500">No executions yet.</p>
          ) : (
            executions.map((ex) => (
              <div key={ex.id} className="rounded-xl border border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
                <div className="flex justify-between gap-2">
                  <span className="font-medium">
                    {ex.status} · {ex.triggerType}
                  </span>
                  <span className="text-xs text-gray-500">
                    {ex.startedAt || ex.createdAt
                      ? new Date(ex.startedAt || ex.createdAt).toLocaleString('en-IN')
                      : '—'}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-600">
                  Eligible {ex.eligibleCount ?? '—'} · Sent {ex.sentCount ?? '—'} · Failed {ex.failedCount ?? '—'} ·
                  Skipped {ex.skippedCount ?? '—'}
                </p>
              </div>
            ))
          )}
        </div>
      ) : null}
    </PushPanelShell>
  );
};

export default PushCampaignDetailPage;
